import { put, takeLeading, select, call } from 'redux-saga/effects';

import notifyBugsnag from '../../../../../util/notifyBugsnag';
import {
  getRestaurantMenu,
  getRestaurantDetails,
  setOrderCart,
} from '../../../../../util/api/RestaurantApiClient';
import {
  getTimeParams,
  getFormattedRestaurantMenu,
} from '../../../../../util/Restaurant';
import formatReorderCartForApi from '../../../../../util/cart';
import * as orderActions from '../../../reducers/ducks/legacy/OrderDuck';
import {
  setReorderResult,
  setReorderError,
  setReorderClosed,
  setReorderScheduling,
  resetReorder,
  toggleItemSelection,
} from '../../../reducers/ducks/legacy/ReorderDuck';
import Notification from '../../../../../components/layout/Notification';
import { searchRestaurants } from '../../../reducers/ducks/legacy/RestaurantsDuck';
import {
  setSelectedRestaurantId,
  setSelectedRestaurantMenu,
} from '../../../reducers/ducks/new/Restaurant/RestaurantReducer';
import { setSelectedAddress } from '../../../reducers/ducks/new/Address/AddressReducer';

function normalizeId(id) {
  if (id == null) return '';
  const str = String(id);
  const m = str.match(/_A(\d+)$/);
  if (m) return m[1];
  return str.replace(/^[IQ]/i, '');
}

function extractAnswerNum(id) {
  if (id == null) return null;
  const m = String(id).match(/A(\d+)/);
  return m ? m[1] : null;
}

function extractQuestionNum(id) {
  if (id == null) return null;
  const m = String(id).match(/Q(\d+)/);
  return m ? m[1] : null;
}

function wordOverlapRatio(a, b) {
  if (!a || !b) return 0;
  const wordsA = a
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length > 2);
  const wordsB = b
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length > 2);
  if (!wordsA.length || !wordsB.length) return 0;
  const setA = new Set(wordsA);
  const setB = new Set(wordsB);
  let intersection = 0;
  setA.forEach((w) => {
    if (setB.has(w)) intersection += 1;
  });
  return intersection / Math.max(setA.size, setB.size);
}

function matchModifiers(originalOptions, menuItem) {
  if (!originalOptions || !originalOptions.length) {
    return { matchedModifiers: [], unavailableModifiers: [] };
  }

  // Bug A fix: answers live as a flat array on menuItem.answers[], not inside questions
  const menuAnswers = menuItem?.answers || [];
  // Bug B fix: display name field is `text`, not `name`
  const byId = new Map(menuAnswers.map((a) => [normalizeId(a.id), a]));

  // Build a multi-value name map so duplicate answer names (e.g. "Small" in two
  // different modifier groups) don't silently overwrite each other.
  const byNormNameMulti = new Map();
  menuAnswers.forEach((a) => {
    const key = (a.text || '').toLowerCase().trim();
    if (!byNormNameMulti.has(key)) {
      byNormNameMulti.set(key, []);
    }
    byNormNameMulti.get(key).push(a);
  });

  const matchedModifiers = [];
  const unavailableModifiers = [];

  originalOptions.forEach((option) => {
    // Tier 1: normalized ID match (extracts _A{n} suffix to handle compound-ID prefix mismatch)
    const normOptionId = normalizeId(option.answer_id);
    if (normOptionId && byId.has(normOptionId)) {
      const ans = byId.get(normOptionId);
      matchedModifiers.push({
        ...ans,
        specialInstructions: option.notes,
        quantity: option.quantity,
      });
      return;
    }

    // Tier 2: exact normalised name — use multi-value map to handle duplicates.
    // When multiple answers share a name, prefer the one whose parent question ID
    // encodes the same question number as the original answer_id (e.g. Q5 in "Q5_A123").
    const normName = (option.answer_text || '').toLowerCase().trim();
    if (normName && byNormNameMulti.has(normName)) {
      const candidates = byNormNameMulti.get(normName);
      const questionNum =
        candidates.length > 1 ? extractQuestionNum(option.answer_id) : null;
      const preferred = questionNum
        ? candidates.find(
            (c) => c.parent && c.parent.includes(`Q${questionNum}`)
          )
        : null;
      const [firstCandidate] = candidates;
      const ans = preferred || firstCandidate;
      matchedModifiers.push({
        ...ans,
        specialInstructions: option.notes,
        quantity: option.quantity,
      });
      return;
    }

    // Tier 3: word overlap >= 80% on text field
    let bestMatch = null;
    let bestScore = 0;
    menuAnswers.forEach((ans) => {
      const score = wordOverlapRatio(option.answer_text, ans.text);
      if (score >= 0.8 && score > bestScore) {
        bestScore = score;
        bestMatch = ans;
      }
    });
    if (bestMatch) {
      matchedModifiers.push({
        ...bestMatch,
        specialInstructions: option.notes,
        quantity: option.quantity,
      });
      return;
    }

    // Tier 4: A{n} suffix search — fallback for when names have drifted
    const answerNum = extractAnswerNum(option.answer_id);
    if (answerNum) {
      const byNum = menuAnswers.find((a) => a.id.includes(`A${answerNum}`));
      if (byNum) {
        matchedModifiers.push({
          ...byNum,
          specialInstructions: option.notes,
          quantity: option.quantity,
        });
        return;
      }
    }

    unavailableModifiers.push(option);
  });

  return { matchedModifiers, unavailableModifiers };
}

function buildItemResult(originalItem, menuItem, itemPrice) {
  const priceChanged =
    originalItem.price != null &&
    itemPrice != null &&
    Math.abs(itemPrice - originalItem.price) > 0.001;

  const hasOptions = originalItem.options && originalItem.options.length > 0;
  let matchedModifiers = [];
  let unavailableModifiers = [];

  if (hasOptions) {
    const modResult = matchModifiers(originalItem.options, menuItem);
    matchedModifiers = modResult.matchedModifiers;
    unavailableModifiers = modResult.unavailableModifiers;
  }

  // Determine if any required modifier group is left unsatisfied.
  // This is checked here at match time so the modal can display the right state
  // before the user clicks through — the applyReorderSaga check remains as a
  // final safety net but this gives users early, actionable feedback.
  const requiredQuestions = (menuItem?.questions || []).filter(
    (q) => q.required
  );
  const matchedParentIds = new Set(matchedModifiers.map((m) => m.parent));
  const hasRequiredMissing =
    requiredQuestions.length > 0 &&
    requiredQuestions.some((q) => !matchedParentIds.has(q.id));

  let status;
  if (hasRequiredMissing) {
    status = 'required_missing';
  } else if (unavailableModifiers.length > 0) {
    status = 'modifiers_changed';
  } else if (priceChanged) {
    status = 'price_changed';
  } else {
    status = 'available';
  }

  // Only pre-select items that are fully matched. Items with modifier changes
  // (optional or required) require explicit user opt-in to prevent silent drops.
  const selected = status === 'available' || status === 'price_changed';

  return {
    originalItem,
    menuItem,
    status,
    selected,
    price: itemPrice ?? originalItem.price,
    priceDelta: priceChanged ? itemPrice - originalItem.price : 0,
    matchedModifiers,
    unavailableModifiers,
  };
}

function matchItems(orderItems, menuItemsMap) {
  if (!orderItems || !menuItemsMap) {
    return [];
  }

  const menuArray = Object.values(menuItemsMap);

  return orderItems.map((item) => {
    // Tier 1: exact item ID
    const byId = menuItemsMap[item.item_id];
    if (byId) {
      return buildItemResult(item, byId, byId.price);
    }

    // Tier 2: exact name (case-insensitive)
    const byName = menuArray.find(
      (m) => m.name?.toLowerCase() === item.name?.toLowerCase()
    );
    if (byName) {
      return buildItemResult(item, byName, byName.price);
    }

    // Tier 3: word overlap >= 60% on significant words (> 2 chars)
    let bestMatch = null;
    let bestScore = 0;
    menuArray.forEach((m) => {
      const score = wordOverlapRatio(item.name, m.name);
      if (score >= 0.6 && score > bestScore) {
        bestScore = score;
        bestMatch = m;
      }
    });
    if (bestMatch) {
      return buildItemResult(item, bestMatch, bestMatch.price);
    }

    return {
      originalItem: item,
      menuItem: null,
      status: 'unavailable',
      selected: false,
      price: item.price,
      priceDelta: 0,
      matchedModifiers: [],
      unavailableModifiers: [],
    };
  });
}

function* fetchMenuItems(order, menuType, timeParams) {
  const orderMode = order.type || 'takeout';
  const orderAddress = order.address;

  const menuParams = {
    client: order.restaurant?.profile,
    mode: orderMode,
    ...(orderMode === 'delivery' && orderAddress
      ? {
          lat: orderAddress.latitude,
          lng: orderAddress.longitude,
        }
      : {}),
    ...(timeParams || {}),
  };

  const rawMenu = yield call(getRestaurantMenu, menuType, menuParams);
  const { menuItems } = yield call(getFormattedRestaurantMenu, rawMenu);
  return menuItems;
}

export function* startReorderSaga({ payload: order }) {
  const restaurantProfile = order.restaurant?.profile;
  try {
    if (!restaurantProfile) {
      yield put(
        setReorderError('Restaurant information is missing from this order.')
      );
      return;
    }

    const menuItems = yield* fetchMenuItems(order, 'asap', null);

    if (!menuItems || !Object.keys(menuItems).length) {
      let details = null;
      try {
        details = yield call(getRestaurantDetails, restaurantProfile);
      } catch (_detailsErr) {
        // eslint-disable-next-line no-empty
      }
      yield put(setReorderScheduling(details));
      return;
    }

    const matched = matchItems(order.items, menuItems);

    const allUnavailable = matched.every((m) => m.status === 'unavailable');
    if (allUnavailable) {
      yield put(
        setReorderError(
          'None of the items from this order are currently available.'
        )
      );
      return;
    }

    yield put(setReorderResult(matched));
  } catch (err) {
    // Note: the Axios response interceptor rejects with err.response, so
    // error fields are at err.status and err.data (not err.response.*)
    const status = err?.status;
    if (status === 404) {
      yield put(setReorderError('This restaurant is no longer available.'));
    } else if (status === 400 || status === 502 || status === 503) {
      let details = null;
      try {
        details = yield call(getRestaurantDetails, restaurantProfile);
      } catch (_detailsErr) {
        // eslint-disable-next-line no-empty
      }
      yield put(setReorderScheduling(details));
    } else {
      notifyBugsnag(err);
      yield put(
        setReorderError(
          'Something went wrong while loading the menu. Please try again.'
        )
      );
    }
  }
}

export function* retryReorderWithTimeSaga({ payload: { start, end } }) {
  try {
    const { order } = yield select(({ reorder: reorderState }) => ({
      order: reorderState.order,
    }));

    if (!order) {
      return;
    }

    const menuItems = yield* fetchMenuItems(order, 'target', { start, end });

    if (!menuItems || !Object.keys(menuItems).length) {
      yield put(
        setReorderClosed(
          'No availability at the selected time. Please choose another time.'
        )
      );
      return;
    }

    const matched = matchItems(order.items, menuItems);

    const allUnavailable = matched.every((m) => m.status === 'unavailable');
    if (allUnavailable) {
      yield put(
        setReorderError(
          'None of the items from this order are available at the selected time.'
        )
      );
      return;
    }

    yield put(setReorderResult(matched));
  } catch (err) {
    const status = err?.status;
    if (status === 404) {
      yield put(setReorderError('This restaurant is no longer available.'));
    } else {
      yield put(
        setReorderClosed(
          'No availability at the selected time. Please choose another time.'
        )
      );
    }
  }
}

export function* applyReorderSaga({ payload }) {
  const { navigate } = payload || {};

  try {
    const { order, matchedItems } = yield select(
      ({ reorder: reorderState }) => ({
        order: reorderState.order,
        matchedItems: reorderState.matchedItems,
      })
    );

    if (!order) {
      return;
    }

    const selectedItems = matchedItems.filter((m) => m.selected && m.menuItem);
    if (!selectedItems.length) {
      yield put(setReorderError('No items are available to reorder.'));
      return;
    }

    // Bug D fix: guard against submitting items that have required questions with no
    // matched modifiers — the cart API validates this server-side and returns 404.
    const blockedItems = [];
    const submittableItems = selectedItems.filter((m) => {
      const requiredQuestions = (m.menuItem?.questions || []).filter(
        (q) => q.required
      );
      if (!requiredQuestions.length) return true;
      const matchedParentIds = new Set(
        (m.matchedModifiers || []).map((mod) => mod.parent)
      );
      const missingRequired = requiredQuestions.some(
        (q) => !matchedParentIds.has(q.id)
      );
      if (missingRequired) {
        blockedItems.push(m);
        return false;
      }
      return true;
    });

    if (!submittableItems.length) {
      const blockedList = blockedItems
        .map((m) => m.menuItem?.name || m.originalItem?.name)
        .join(', ');
      yield put(
        setReorderError(
          `Required options could not be matched for: ${blockedList}. Please add these items manually from the restaurant page.`
        )
      );
      return;
    }

    if (blockedItems.length > 0) {
      // Auto-uncheck blocked items in Redux state so the modal reflects the change
      for (let i = 0; i < blockedItems.length; i += 1) {
        const idx = matchedItems.indexOf(blockedItems[i]);
        if (idx >= 0) {
          yield put(toggleItemSelection(idx));
        }
      }
      const blockedList = blockedItems
        .map((m) => m.menuItem?.name || m.originalItem?.name)
        .join(', ');
      Notification.warning(
        `Some items were removed because required options could not be matched: ${blockedList}. You can add them manually from the restaurant page.`
      );
    }

    const itemsForApi = submittableItems.map((m) => {
      const newItemId = m.menuItem.id ?? m.originalItem.item_id;
      const apiOptions = (m.matchedModifiers || []).map((mod) => ({
        answer_id: mod.id,
        answer_text: mod.text,
        notes: mod.specialInstructions,
        quantity: mod.quantity || 1,
      }));
      return {
        ...m.originalItem,
        item_id: newItemId,
        options: apiOptions,
      };
    });

    const cartData = formatReorderCartForApi(itemsForApi, []);

    const orderMode = order.type || 'takeout';
    const orderAddress = order.address;

    const { currentTime, userId, orderMinimums } = yield select(
      ({ order: orderData, main }) => ({
        currentTime: orderData.time,
        userId: main.user?.id || main.user?.data?.id,
        orderMinimums: orderData.minimums,
      })
    );

    // Pre-flight minimum check: if selected items don't meet the order minimum,
    // close the modal, show a clear toast, and navigate to the restaurant page
    // so the user can add more items — never send them to a checkout that will fail.
    // Use orderMode (derived from the original reorder order type) as the key
    // so the check is always evaluated against the correct mode's minimum.
    const minimum = orderMinimums?.[orderMode] || 0;
    if (minimum > 0) {
      const selectedTotal = submittableItems.reduce((sum, m) => {
        const price = m.menuItem?.price ?? m.originalItem.price ?? 0;
        return sum + price * (m.originalItem.quantity || 1);
      }, 0);
      if (selectedTotal < minimum) {
        const totalStr = selectedTotal.toFixed(2);
        const minStr = minimum.toFixed(2);
        const modeLabel = orderMode === 'delivery' ? 'delivery' : 'pickup';
        yield put(resetReorder());
        Notification.warning(
          `Your selected items ($${totalStr}) don't meet the $${minStr} ${modeLabel} minimum. Add more items to continue.`,
          { toastId: 'reorder-below-minimum' }
        );
        if (navigate) {
          yield call(navigate, `/restaurant/${order.restaurant.profile}`);
        }
        return;
      }
    }

    const timeParams = getTimeParams(currentTime) || { type: 'asap' };

    // cart/set only accepts address_id — lat/lng are not valid fields.
    // The Eatzy order history API returns the address ID as address.id (integer),
    // not address.address_id. Fall back to address_id for forward-compatibility,
    // then fall back to the user's primary saved address if neither field is present.
    let locationData = {};
    if (orderMode === 'delivery') {
      const resolvedAddressId = orderAddress?.address_id ?? orderAddress?.id;
      if (resolvedAddressId) {
        locationData = { address: resolvedAddressId };
      } else {
        const { savedAddresses } = yield select(({ profile }) => ({
          savedAddresses: profile.getAddressesResponse?.response || [],
        }));
        const fallbackAddr =
          savedAddresses.find((a) => a.primary) || savedAddresses[0];
        if (fallbackAddr) {
          locationData = { address: fallbackAddr.id };
        } else {
          yield put(
            setReorderError(
              'Please add a saved delivery address to your account to reorder this delivery.'
            )
          );
          return;
        }
      }
    }

    const apiData = {
      client: order.restaurant.profile,
      data: {
        mode: orderMode,
        cart: cartData,
        ...locationData,
        ...timeParams,
      },
    };

    yield call(setOrderCart, apiData);

    const {
      restaurant: { profile },
    } = order;

    yield put(setSelectedRestaurantId(profile));

    const typeData = {
      label: orderMode === 'delivery' ? 'Delivery' : 'Pickup',
      value: orderMode,
      text: orderMode === 'delivery' ? 'Delivery' : 'Pickup',
    };
    yield put(orderActions.setOrderType(typeData));

    const reorderDate = order.date
      ? new Date(order.date * 1000).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    yield put(orderActions.setOrderDate(reorderDate));

    const reorderTime = order.time || { label: 'ASAP', value: 'asap' };
    yield put(orderActions.setOrderTime(reorderTime));

    if (orderAddress) {
      const formattedAddress = {
        entered_address: `${orderAddress.address}, ${orderAddress.city}, ${orderAddress.state} ${orderAddress.zipcode}`,
        address: orderAddress.address,
        city: orderAddress.city,
        state: orderAddress.state,
        zip: orderAddress.zipcode,
        latitude: orderAddress.latitude
          ? parseFloat(String(orderAddress.latitude))
          : undefined,
        longitude: orderAddress.longitude
          ? parseFloat(String(orderAddress.longitude))
          : undefined,
      };
      yield put(orderActions.setOrderAddress(formattedAddress));

      if (orderMode === 'delivery' && locationData.address) {
        yield put(
          setSelectedAddress({
            ...formattedAddress,
            zipcode: orderAddress.zipcode,
            address_id: locationData.address,
            user_id: userId,
          })
        );
      }
    }

    const menuItemsMap = {};
    submittableItems.forEach((m) => {
      if (m.menuItem?.id != null) {
        menuItemsMap[m.menuItem.id] = m.menuItem;
      }
    });
    yield put(setSelectedRestaurantMenu({ menuItems: menuItemsMap }));

    const cartItems = submittableItems.map((m, idx) => ({
      comment: m.originalItem.notes,
      quantity: m.originalItem.quantity,
      name: m.menuItem.name || m.originalItem.name,
      answers: (m.matchedModifiers || []).map((mod) => {
        const question =
          m.menuItem?.questions?.find((q) => q.id === mod.parent) || {};
        return {
          ...mod,
          order: question.order,
          weight: mod.weight || 1,
        };
      }),
      sort: idx + 1,
      menuItemId: m.menuItem.id ?? m.originalItem.item_id,
      price: m.menuItem.price ?? m.originalItem.price,
    }));

    yield put(orderActions.setCartItems(cartItems));

    const { restaurantsData } = yield select(({ restaurants }) => ({
      restaurantsData: restaurants.restaurants,
    }));
    if (!Object.keys(restaurantsData || {}).length) {
      yield put(searchRestaurants());
    }

    yield put(resetReorder());

    if (navigate) {
      yield call(navigate, '/checkout');
    }
  } catch (err) {
    // Note: the Axios response interceptor rejects with err.response, so
    // error fields are at err.status and err.data (not err.response.*)
    const applyStatus = err?.status;
    const applyErrMsg = err?.data?.message || '';
    if (
      applyStatus === 400 &&
      applyErrMsg.toLowerCase().includes('not available')
    ) {
      yield put(
        setReorderClosed(
          'This restaurant is currently closed. Please try again later.'
        )
      );
    } else {
      notifyBugsnag(err);
      yield put(
        setReorderError(
          'Something went wrong while placing the reorder. Please try again.'
        )
      );
    }
  }
}

export function* watchReorderSagas() {
  yield takeLeading('reorder/startReorder', startReorderSaga);
  yield takeLeading('reorder/retryReorderWithTime', retryReorderWithTimeSaga);
  yield takeLeading('reorder/applyReorder', applyReorderSaga);
}
